import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    ConsentDataResponse,
    UpdateConsentRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/consent/api/consent.api.ts';
import {
    getApiConsent,
    updateApiConsent,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/consent/api/consent.api.ts';
import { CONSENT_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/consent/constants.ts';
import {
    hasConsentDataChanged,
    wasStepAlreadyPublished,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/consent/utils.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import { extractError } from '@utils/slice.ts';

export interface ConsentError {
    message?: string;
    [k: string]: any;
}

export interface ConsentState {
    error: ConsentError | null;
    consentData: ConsentDataResponse | null;
    isAddNewConsentItemModalOpened: boolean;
    isEditConsentItemModalOpened: boolean;
    isDeleteConsentItemModalOpened: boolean;
    currentConsentId: number | null;
    isGetConsentPending: boolean;
    isUpdateConsentPending: boolean;
    isSaveDisabled: boolean;
    initialDataSnapshot: ConsentDataResponse | null;
}

const initialState: ConsentState = {
    error: null,
    consentData: null,
    isAddNewConsentItemModalOpened: false,
    isEditConsentItemModalOpened: false,
    isDeleteConsentItemModalOpened: false,
    currentConsentId: null,
    isGetConsentPending: false,
    isUpdateConsentPending: false,
    isSaveDisabled: true,
    initialDataSnapshot: null,
};

export const getConsent = createAsyncThunk<
    ConsentDataResponse,
    { clientId: number; tenantAlias: string },
    { rejectValue: ConsentError }
>('adminCenterConsent/getApiConsent', ({ clientId, tenantAlias }, { rejectWithValue, dispatch }) =>
    getApiConsent(clientId, tenantAlias)
        .then((res) => {
            if (wasStepAlreadyPublished(res)) {
                dispatch(setPublishedSteps(CONSENT_STEP_TITLE));
            }

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const updateConsent = createAsyncThunk<
    any,
    { clientId: number; tenantAlias: string; payload: UpdateConsentRequest },
    { rejectValue: ConsentError }
>('adminCenterConsent/updateApiConsent', ({ clientId, tenantAlias, payload }, { rejectWithValue, dispatch }) =>
    updateApiConsent(clientId, tenantAlias, payload)
        .then((res) => {
            dispatch(setPublishedSteps(CONSENT_STEP_TITLE));

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

const consentSlice = createSlice({
    name: 'adminCenterConsent',
    initialState,
    reducers: {
        setIsAddNewConsentItemModalOpened(state, action: PayloadAction<boolean>) {
            state.isAddNewConsentItemModalOpened = action.payload;
        },
        setIsEditConsentItemModalOpened(state, action: PayloadAction<boolean>) {
            state.isEditConsentItemModalOpened = action.payload;
        },
        setIsDeleteConsentItemModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeleteConsentItemModalOpened = action.payload;
        },
        setCurrentConsentId(state, action: PayloadAction<number | null>) {
            state.currentConsentId = action.payload;
        },
        setConsentData(state, action: PayloadAction<ConsentDataResponse>) {
            state.consentData = action.payload;

            if (state.initialDataSnapshot) {
                const hasChanges = hasConsentDataChanged(state.consentData, state.initialDataSnapshot);
                state.isSaveDisabled = !hasChanges;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getConsent.pending, (state) => {
            state.isGetConsentPending = true;
            state.error = null;
        });
        builder.addCase(getConsent.fulfilled, (state, action) => {
            state.isGetConsentPending = false;
            state.consentData = action.payload;

            if (state.initialDataSnapshot === null) {
                state.initialDataSnapshot = action.payload;
                state.isSaveDisabled = true;
            } else {
                const hasChanges = hasConsentDataChanged(state.consentData, state.initialDataSnapshot);
                state.isSaveDisabled = !hasChanges;
            }
        });

        builder.addCase(getConsent.rejected, (state, action) => {
            state.isGetConsentPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateConsent.pending, (state) => {
            state.isUpdateConsentPending = true;
            state.error = null;
        });
        builder.addCase(updateConsent.fulfilled, (state) => {
            state.isUpdateConsentPending = false;
            state.initialDataSnapshot = null;
            state.isSaveDisabled = true;
        });

        builder.addCase(updateConsent.rejected, (state, action) => {
            state.isUpdateConsentPending = false;
            state.error = extractError(action);
        });
    },
});

export const {
    setIsAddNewConsentItemModalOpened,
    setIsEditConsentItemModalOpened,
    setIsDeleteConsentItemModalOpened,
    setCurrentConsentId,
    setConsentData,
} = consentSlice.actions;
export default consentSlice.reducer;
